import React, { useEffect, useMemo, useState } from 'react';
import { ChartLineUp } from '@phosphor-icons/react';
import { useI18n } from '../../contexts/I18nContext';
import { useOrg } from '../../contexts/OrgContext';
import { useAuth } from '../../contexts/AuthContext';
import { getMovementHistory } from '../../lib/movement';
import { nightWindow } from '../../lib/nightReport';
import { lastNights } from '../../lib/movementView';
import { fmtInTz } from '../../lib/tz';
import { useNightData, buildSubjects } from './useNightData';
import { WeekTrendRow } from './WeekTrendRow';
import { DayBarLegend } from '../movement/DayBar';

const NIGHTS = 7;

export function WeekTrend() {
  const { t, lang } = useI18n();
  const { current } = useOrg();
  const { user } = useAuth();
  const base = useNightData(current?.id);
  const [hist, setHist] = useState(null);
  const now = useMemo(() => Date.now(), [base]); // eslint-disable-line react-hooks/exhaustive-deps
  const span = useMemo(() => (base ? { first: nightWindow(base.policy, now, NIGHTS - 1), last: nightWindow(base.policy, now, 0) } : null), [base, now]);

  useEffect(() => {
    if (!span) return undefined;
    let alive = true;
    setHist(null);
    getMovementHistory({ from: new Date(span.first.fromMs).toISOString(), to: new Date(span.last.toMs).toISOString(), resolution: '5m' })
      .then((b) => alive && setHist(b)).catch(() => alive && setHist('error'));
    return () => { alive = false; };
  }, [span]);

  if (!base || !span) return <div className="text-ink-muted py-10 text-center">{t('loading')}</div>;
  const tz = span.first.tz;
  const subjects = buildSubjects(base, { family: user?.role === 'family' }).subjects;

  return (
    <div className="space-y-5" data-testid="week-trend">
      <div className="flex items-center justify-between gap-3 flex-wrap bg-white border border-stone_line rounded-2xl shadow-soft px-5 py-4">
        <div className="flex items-center gap-3">
          <ChartLineUp size={22} weight="duotone" className="text-sage-600" />
          <div>
            <div className="font-heading text-xl text-ink" data-testid="week-trend-range">
              {fmtInTz(span.first.fromMs, tz, { day: '2-digit', month: 'short' }, lang)} → {fmtInTz(span.last.toMs, tz, { day: '2-digit', month: 'short' }, lang)}
            </div>
            <div className="text-xs text-ink-muted">
              {base.policy.night_hours_start.slice(0, 5)}–{base.policy.night_hours_end.slice(0, 5)} · {tz} · {t('wt_baseline_note')}
            </div>
          </div>
        </div>
        <DayBarLegend night={false} />
      </div>
      {hist === 'error' && (
        <div className="bg-terracotta-50 text-terracotta-700 rounded-xl p-4 text-sm" data-testid="week-trend-error">{t('wt_load_failed')}</div>
      )}
      {hist === 'error' ? null : subjects.length === 0 ? (
        <div className="text-ink-muted py-10 text-center" data-testid="week-trend-empty">{t('wt_no_sensors')}</div>
      ) : subjects.map((sub) => (
        <WeekTrendRow key={`${sub.kind}-${sub.id}`} subject={sub} buckets={hist}
          nights={Array.isArray(hist) ? lastNights(hist, sub.ids, base.policy, now, NIGHTS, 0) : null} />
      ))}
    </div>
  );
}
